import React from "react";
import { cn } from "@/lib/utils";
import { skipToMain } from "@/utils/accessibility.jsx";

/**
 * Skip Navigation Component
 * WCAG 2.1 AA Compliant
 *
 * Provides keyboard users with quick navigation to main content areas
 * Becomes visible when focused via keyboard navigation
 */
const SkipNavigation = ({ links = [], className, ...props }) => {
  // Default links if none provided
  const defaultLinks = [
    { href: "#main-content", text: "Skip to main content" },
    { href: "#navigation", text: "Skip to navigation" },
    { href: "#footer", text: "Skip to footer" },
  ];

  const navigationLinks = links.length > 0 ? links : defaultLinks;

  const handleClick = (e, targetId) => {
    e.preventDefault();
    const id = targetId.replace("#", "");
    skipToMain(id);
  };

  return (
    <nav
      className={cn("skip-navigation", className)}
      aria-label="Skip navigation"
      {...props}
    >
      <ul className="skip-navigation-list">
        {navigationLinks.map((link, index) => (
          <li key={index}>
            <a
              href={link.href}
              className={cn(
                "skip-navigation-link",
                "absolute left-0 top-0 z-[100]",
                "bg-primary text-primary-foreground",
                "px-4 py-2 m-2 rounded-md",
                "transform -translate-y-16",
                "transition-transform duration-200",
                "focus:translate-y-0",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "text-sm font-medium",
                "no-underline hover:underline",
                "shadow-lg",
                link.className,
              )}
              onClick={(e) => handleClick(e, link.href)}
            >
              {link.text}
            </a>
          </li>
        ))}
      </ul>

      <style>{`
        /* Ensure skip links are hidden but accessible */
        .skip-navigation {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }

        /* Make the container visible when any child link is focused */
        .skip-navigation:focus-within {
          position: static;
          width: auto;
          height: auto;
          padding: 0;
          margin: 0;
          overflow: visible;
          clip: auto;
          white-space: normal;
        }

        .skip-navigation-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        /* Additional styles for better visibility when focused */
        .skip-navigation-link:focus {
          position: fixed;
          top: 0;
          left: 0;
          z-index: 9999;
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .skip-navigation-link:focus {
            outline: 3px solid;
            outline-offset: 2px;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .skip-navigation-link {
            transition: none;
          }
        }

        /* Print styles - hide skip navigation */
        @media print {
          .skip-navigation {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
};

/**
 * Main Content Landmark Component
 * Use this to wrap your main content area
 */
export const MainContent = ({
  children,
  id = "main-content",
  className,
  ...props
}) => {
  return (
    <main id={id} className={className} tabIndex={-1} {...props}>
      {children}
    </main>
  );
};

/**
 * Navigation Landmark Component
 * Use this to wrap your navigation area
 */
export const NavigationLandmark = ({
  children,
  id = "navigation",
  label = "Main navigation",
  className,
  ...props
}) => {
  return (
    <nav
      id={id}
      className={className}
      aria-label={label}
      tabIndex={-1}
      {...props}
    >
      {children}
    </nav>
  );
};

/**
 * Footer Landmark Component
 * Use this to wrap your footer area
 */
export const FooterLandmark = ({
  children,
  id = "footer",
  className,
  ...props
}) => {
  return (
    <footer id={id} className={className} tabIndex={-1} {...props}>
      {children}
    </footer>
  );
};

/**
 * Hook for managing skip navigation
 */
export const useSkipNavigation = () => {
  const [skipLinks, setSkipLinks] = React.useState([]);

  const addSkipLink = React.useCallback((link) => {
    setSkipLinks((prev) => [...prev, link]);
  }, []);

  const removeSkipLink = React.useCallback((href) => {
    setSkipLinks((prev) => prev.filter((link) => link.href !== href));
  }, []);

  const updateSkipLink = React.useCallback((href, updates) => {
    setSkipLinks((prev) =>
      prev.map((link) => (link.href === href ? { ...link, ...updates } : link)),
    );
  }, []);

  return {
    skipLinks,
    addSkipLink,
    removeSkipLink,
    updateSkipLink,
  };
};

/**
 * Example usage in App.jsx:
 *
 * import SkipNavigation, { MainContent } from '@/components/SkipNavigation';
 *
 * function App() {
 *   return (
 *     <>
 *       <SkipNavigation />
 *       <header>...</header>
 *       <MainContent>
 *         <Routes>...</Routes>
 *       </MainContent>
 *       <footer>...</footer>
 *     </>
 *   );
 * }
 */

export default SkipNavigation;
